import { idOf, completionPhase } from "./tradeUiLogic";

/**
 * tradeJourneyLogic — the five-checkpoint model behind the trades redesign.
 *
 * Presentation stage is DERIVED from a trade's own fields, never stored as a
 * status string and never held app-wide. That is what lets six trades sit in one
 * inbox without a global "current trade": each card asks these functions about
 * itself.
 *
 * Role-aware throughout — the same trade is at a different point for the sender
 * and the receiver, and saying "waiting" to the person who is actually being
 * waited on is the bug this replaces.
 */

export const STAGE = { OFFER: 0, ACCEPTED: 1, MEETUP: 2, NEARBY: 3, EXCHANGED: 4 };

/**
 * Statuses a trade cannot progress out of.
 *
 * `disputed` is not one of them — see `isOnHold`. Listing it here made a
 * reported trade render as a dead one: "Offer accepted" lost its time and took
 * the padlock, and the rail fell back to step 1.
 */
export const TERMINAL = new Set([
  "declined",
  "cancelled",
  "expired",
  "abandoned",
]);

/**
 * On hold: `disputed`, reached through "Report a problem". Paused while the
 * team reviews it, not ended — an admin can release it. It keeps every
 * checkpoint its own evidence proves it reached, and none of the rest is
 * current, waiting or actionable: the page is telling both traders not to meet
 * for it.
 */
export const isOnHold = (trade) => trade?.status === "disputed";

/** Checkpoint presentation states. */
export const CP = {
  DONE: "done",
  CURRENT: "current",
  WAITING: "waiting",
  LOCKED: "locked",
  MUTED: "muted",
  UPCOMING: "upcoming",
};

/**
 * How far along the trade is, as a checkpoint index.
 *
 * 5 = every checkpoint done, -1 = terminal (declined/cancelled/…) so a dead
 * trade never renders as "in progress".
 *
 * An on-hold trade reads the same evidence, with `acceptedAt` standing in for
 * the `accepted` status it no longer has, so its index is how far it GOT.
 * `checkpointsFor` and `nextActionFor` make sure nothing past it is in progress.
 */
export function stageOf(trade) {
  if (!trade) return -1;
  if (trade.status === "completed") return 5;
  if (TERMINAL.has(trade.status)) return -1;
  if (trade.presenceVerifiedAt) return STAGE.EXCHANGED;
  if (trade.meetup?.agreedAt) return STAGE.NEARBY;
  if (trade.status === "accepted" || (isOnHold(trade) && trade.acceptedAt)) return STAGE.MEETUP;
  return STAGE.ACCEPTED;
}

const iAmSenderOf = (trade, myId) => idOf(trade?.senderId) === String(myId);

/** Did I already confirm the physical exchange? */
const myConfirm = (trade, myId) =>
  iAmSenderOf(trade, myId) ? !!trade?.senderCompleted : !!trade?.receiverCompleted;

/** Did the OTHER collector confirm? */
const theirConfirm = (trade, myId) =>
  iAmSenderOf(trade, myId) ? !!trade?.receiverCompleted : !!trade?.senderCompleted;

/**
 * May I still say the meetup fell through? Owner decision Q-24 (2026-10-05):
 * not once the OTHER collector has confirmed the swap. Then I can confirm it
 * too or report a problem, and the server refuses the abandon with
 * PARTNER_CONFIRMED_NOTE. Keyed on their confirmation, never mine: having
 * confirmed myself does not close my own exit.
 */
export const canMarkFellThrough = (trade, myId) =>
  trade?.status === "accepted" && !theirConfirm(trade, myId);

/** The server's own refusal sentences (server/trades/tradeStatusCopy.js). */
export const PARTNER_CONFIRMED_NOTE =
  "The other trader has already confirmed the swap. Confirm it too, or report a problem.";
export const BOTH_CONFIRMED_NOTE =
  "You have both confirmed the swap, but it couldn't be completed. Report a problem so we can sort it out.";

/**
 * Why "Meetup fell through" is gone from an accepted trade, or null while it is
 * still offered. A trader who has confirmed too is not told to "confirm it
 * too": both flags set means a completion was refused, and a report is left.
 */
export const fellThroughClosedNote = (trade, myId) => {
  if (trade?.status !== "accepted" || canMarkFellThrough(trade, myId)) return null;
  return myConfirm(trade, myId) ? BOTH_CONFIRMED_NOTE : PARTNER_CONFIRMED_NOTE;
};

/**
 * Which sides have checked in — two booleans, from what the API actually sends.
 *
 * This used to read `trade.checkIns?.sender?.at` directly. That subdocument is
 * `select: false` on the server BECAUSE it holds the meetup's raw coordinates,
 * and the trade routes never asked for it, so the field was undefined on every
 * response this app has ever received: a one-sided check-in was invisible, and
 * refreshing the page invited you to check in again as if nothing had happened.
 *
 * `checkInState` is the server's derived, coordinate-free answer to exactly the
 * question this file asks. Nothing here wants a position, a distance or even a
 * time — only whether each side has arrived.
 *
 * The raw shape is still read when the derived field is absent, so a caller that
 * legitimately holds a full trade document is not silently told "nobody checked
 * in". Only the `at` timestamps are ever consulted, never a coordinate.
 */
export function checkInSides(trade) {
  const derived = trade?.checkInState;
  if (derived && typeof derived === "object") {
    return { sender: !!derived.sender, receiver: !!derived.receiver };
  }
  return {
    sender: !!trade?.checkIns?.sender?.at,
    receiver: !!trade?.checkIns?.receiver?.at,
  };
}

/** Have I checked in? Role-aware, like everything else here. */
const iCheckedInOn = (trade, myId) => {
  const sides = checkInSides(trade);
  return iAmSenderOf(trade, myId) ? sides.sender : sides.receiver;
};

/**
 * Is this trade subject to the presence gate?
 *
 * The server decides, and this reads its answer — nothing else. The gate depends
 * on TRADE_PRESENCE_ENFORCED_FROM, a date the client never sees, so any rule
 * here is a copy that can only ever drift from the one the server enforces.
 *
 * There used to be such a copy: a second parameter carrying the flag date, with
 * the acceptedAt comparison repeated. It was reachable only through a
 * `presenceEnforcedFrom` prop that NO caller ever passed, so on the list-shaped
 * trades in the inbox — which carried no `presenceRequired` either — both
 * branches fell through and every trade answered "not gated". The inbox then
 * showed the check-in as skippable and pointed its primary button at completion
 * while the journey page, holding the same trade from the detail route, showed
 * it as required (P2-42).
 *
 * `GET /trades` and `GET /trades/:tradeId` now stamp `presenceRequired` from one
 * server-side helper, so the field is present on every trade this app receives.
 * A trade without it is a shape we do not expect rather than a legacy trade, and
 * "not gated" is the only safe reading of silence: claiming a gate the server is
 * not enforcing would lock the user out of completing a trade with no check-in
 * that could ever satisfy it.
 *
 * Trades accepted before the gate shipped have no way to satisfy it, and
 * stranding a real meet-up already in flight would be worse than the gap it
 * closes. The server answers `false` for those, and they render checkpoint 4 as
 * a muted "not required" row rather than a lock, so the rail stays honest about
 * why it is skippable.
 */
export function isPresenceGated(trade) {
  return trade?.presenceRequired === true;
}

/**
 * The five checkpoints for this trade, from this user's point of view.
 *
 * Each carries its own state and the timestamp that justifies it, so the rail
 * never has to claim something happened without being able to say when.
 */
export function checkpointsFor(trade, myId) {
  const stage = stageOf(trade);
  const terminal = stage === -1;
  const onHold = isOnHold(trade);
  const iAmSender = iAmSenderOf(trade, myId);
  // A STATUS, not "was ever accepted": checkpoints 3-5 can only be current,
  // waiting, locked or muted behind it. An on-hold trade is neither, so those
  // three show done where its evidence says so and upcoming everywhere else.
  const accepted = trade?.status === "accepted" || trade?.status === "completed";
  const completed = trade?.status === "completed";
  const gated = isPresenceGated(trade);
  const mineConfirmed = myConfirm(trade, myId);

  // 1 — the offer itself. Always done: the trade exists because it was sent.
  const offer = {
    key: "offer",
    label: iAmSender ? "Offer sent" : "Offer received",
    state: CP.DONE,
    at: trade?.createdAt || null,
  };

  // 2 — acceptance. The receiver is the one who can act; the sender waits.
  // On hold: done if the trade got past it, else upcoming — never an Accept
  // button, and never the padlock of a dead trade.
  const acceptance = {
    key: "accepted",
    label: "Offer accepted",
    state:
      accepted || (onHold && stage >= STAGE.MEETUP)
        ? CP.DONE
        : onHold
        ? CP.UPCOMING
        : terminal
        ? CP.MUTED
        : iAmSender
        ? CP.WAITING
        : CP.CURRENT,
    at: accepted || onHold ? trade?.acceptedAt || null : null,
  };

  // 3 — a venue and time agreed between them, in the trade's own chat.
  const meetupAgreed = !!trade?.meetup?.agreedAt;
  const meetup = {
    key: "meetup",
    label: "Meetup arranged",
    // A COMPLETED trade has nothing current or pending: "you are here" on a
    // finished trade is incoherent. But it is not DONE either unless a meetup
    // was actually agreed (B-26): the cards can change hands without one, and a
    // tick here is the app's own record claiming an arrangement nobody made. So
    // a finished trade with no agreed meetup reads as skipped, with a note.
    state: meetupAgreed
      ? CP.DONE
      : completed
      ? CP.MUTED
      : !accepted || terminal
      ? CP.UPCOMING
      : CP.CURRENT,
    at: trade?.meetup?.agreedAt || null,
    venueName: trade?.meetup?.venueName || null,
    note: completed && !meetupAgreed ? "Not arranged in the app" : null,
  };

  // 4 — both physically present. Never derived from a map pin or publicLocation.
  const iCheckedIn = iCheckedInOn(trade, myId);
  let nearbyState;
  if (trade?.presenceVerifiedAt) nearbyState = CP.DONE;
  // Finished without a verified check-in: nothing is still pending, and nothing
  // proves they were both there either (B-26), so it is skipped, not ticked.
  else if (completed) nearbyState = CP.MUTED;
  else if (!accepted || terminal) nearbyState = CP.UPCOMING;
  else if (!gated) nearbyState = CP.MUTED; // legacy trade — honestly skippable
  else if (iCheckedIn) nearbyState = CP.WAITING;
  else nearbyState = CP.CURRENT;

  const nearby = {
    key: "nearby",
    label: "Both collectors nearby",
    state: nearbyState,
    at: trade?.presenceVerifiedAt || null,
    note:
      nearbyState !== CP.MUTED
        ? null
        : gated
        ? "No check-in recorded"
        : "Not required for this trade",
    method: trade?.presenceVerificationMethod || null,
  };

  // 5 — the physical swap. `done` ONLY on a completed trade: a one-sided
  // confirm is waiting, never done.
  let exchangedState;
  if (completed) exchangedState = CP.DONE;
  else if (!accepted || terminal) exchangedState = CP.UPCOMING;
  else if (mineConfirmed) exchangedState = CP.WAITING;
  else if (gated && !trade?.presenceVerifiedAt) exchangedState = CP.LOCKED;
  else exchangedState = CP.CURRENT;

  const exchanged = {
    key: "exchanged",
    label: "Cards exchanged",
    state: exchangedState,
    at: completed ? trade?.completedAt || null : null,
  };

  return [offer, acceptance, meetup, nearby, exchanged];
}

/**
 * B-12: is a card on this PENDING offer already promised to another accepted
 * trade? Read from the server's `promisedElsewhere`, which an old server does
 * not send, and then the answer is simply "no", as it always was.
 */
export const isPromisedElsewhere = (trade) =>
  trade?.status === "pending" && trade.promisedElsewhere?.length > 0;

const PROMISED_HINT = "A card in this offer is promised to another trade";

/**
 * The single next thing this user can do, for the inbox card's primary button.
 *
 * `tone` drives loudness: only `act` earns the violet border, so at most a
 * third of a screenful shouts.
 */
export function nextActionFor(trade, myId) {
  if (!trade) return null;
  const stage = stageOf(trade);
  const iAmSender = iAmSenderOf(trade, myId);

  // On hold has a stage now, but it is no more actionable than a dead trade —
  // falling through would offer "Confirm exchange" on a trade under review.
  if (stage === -1 || isOnHold(trade)) {
    return { key: "none", label: null, tone: "muted", hint: statusLabel(trade.status) };
  }
  if (trade.status === "completed") {
    return { key: "review", label: "Review your collection", tone: "done", hint: "Trade complete" };
  }
  // B-07: one of them blocked the other (`blocked`, from the server; an old
  // server sends nothing and this never fires). Chat, meetups and Accept are all
  // refused now, so "Arrange meetup" sent people in a circle. The trade page
  // says why and how to end it; a sender can still cancel right here. An
  // accepted trade says only what stopped: a meetup agreed before the block can
  // still go ahead (check-in and Confirm stay open on the server).
  if (trade.blocked) {
    const hint = "You can no longer trade with this collector";
    if (trade.status === "accepted") {
      return { key: "blocked", label: "See options", tone: "wait", hint: "You can no longer message this collector" };
    }
    return iAmSender
      ? { key: "cancel", label: "Cancel offer", tone: "wait", hint }
      : { key: "blocked_offer", label: "Review offer", tone: "wait", hint };
  }
  if (trade.status === "pending") {
    // B-12: a card in it is already promised to another ACCEPTED trade
    // (`promisedElsewhere`, stamped by the server from the same check its accept
    // refuses with). Accepting it now is a guaranteed refusal, so the receiver
    // is not handed an inline Accept: "Review offer" opens the trade, which says
    // why. Decline and Cancel stay. The mark clears when that trade ends.
    if (isPromisedElsewhere(trade)) {
      return iAmSender
        ? { key: "cancel", label: "Cancel offer", tone: "wait", hint: PROMISED_HINT }
        : { key: "promised", label: "Review offer", tone: "wait", hint: PROMISED_HINT };
    }
    return iAmSender
      ? { key: "cancel", label: "Cancel offer", tone: "wait", hint: "Waiting for their reply" }
      : { key: "respond", label: "Review offer", tone: "act", hint: "They're waiting on you" };
  }

  // accepted
  const gated = isPresenceGated(trade);
  if (myConfirm(trade, myId)) {
    return {
      key: "waiting_confirm",
      label: null,
      tone: "wait",
      hint: theirConfirm(trade, myId) ? "Finishing up" : "Waiting for them to confirm",
    };
  }
  if (theirConfirm(trade, myId)) {
    // They have confirmed and I have not — this is the definition of "needs you".
    return gated && !trade.presenceVerifiedAt
      ? { key: "checkin", label: "Check in at meetup", tone: "act", hint: "They've confirmed" }
      : { key: "confirm", label: "Confirm exchange", tone: "act", hint: "They've confirmed" };
  }
  if (gated && !trade.presenceVerifiedAt) {
    return { key: "checkin", label: "Check in at meetup", tone: "act", hint: "Meet up to continue" };
  }
  if (!trade.meetup?.agreedAt) {
    return { key: "arrange", label: "Arrange meetup", tone: "act", hint: "Agree a place and time" };
  }
  return { key: "confirm", label: "Confirm exchange", tone: "act", hint: "Confirm once you've swapped" };
}

/** Human label for a terminal status, so history rows explain themselves. */
export function statusLabel(status) {
  switch (status) {
    case "declined": return "Declined";
    case "cancelled": return "Cancelled";
    case "expired": return "Expired";
    case "abandoned": return "Abandoned";
    // "On hold", not "Disputed": the other trader sees this label too, and it
    // must not read as an accusation (P0 area D).
    case "disputed": return "On hold";
    case "completed": return "Completed";
    case "accepted": return "Accepted";
    case "pending": return "Pending";
    default: return "";
  }
}

/**
 * Where this user stands in the presence check-in, for the journey's check-in
 * panel. Kept here (not in the component) so every branch is testable.
 */
export function presenceStateFor(trade, myId) {
  if (!trade) return "unavailable";
  if (trade.presenceVerifiedAt) return "verified";
  if (trade.status !== "accepted") return "unavailable";
  if (!isPresenceGated(trade)) return "not_required";
  const iAmSender = iAmSenderOf(trade, myId);
  const sides = checkInSides(trade);
  const mine = iAmSender ? sides.sender : sides.receiver;
  const theirs = iAmSender ? sides.receiver : sides.sender;
  // Both checked in and it still has not verified. Deliberately NOT called
  // "too_far": this is derived from the trade document, which carries no reason,
  // and since mixed-method check-ins now verify (P1-03) the usual cause is an
  // expired window rather than distance. Naming a cause we cannot know is how
  // the old label would have produced confidently wrong copy.
  if (mine && theirs) return "unverified";
  if (mine) return "waiting_for_them";
  return "ready";
}

/**
 * What the step in progress is waiting for. A checkpoint's `label` is its DONE
 * tense, so naming the step in progress by it announced it as already done —
 * "Step 2 of 5: offer accepted" on an offer nobody had answered yet.
 */
const TO_DO = {
  accepted: "accept",
  meetup: "agree a place and time",
  nearby: "check in",
  exchanged: "confirm the exchange",
};

/** `aria-label` for the 5-dot rail — the rail is decorative without it. */
export function progressLabel(trade, myId) {
  const checkpoints = checkpointsFor(trade, myId);
  const at = checkpoints.findIndex((c) => c.state === CP.CURRENT || c.state === CP.WAITING);
  if (at !== -1) {
    const { key, state } = checkpoints[at];
    // WAITING only means I have acted. When they have too, nobody is waiting
    // on them: the check-in didn't confirm, or the finish is still settling.
    if (state === CP.WAITING && key === "nearby" && presenceStateFor(trade, myId) === "unverified") {
      return "Step 4 of 5: both checked in but it didn't confirm, check in again";
    }
    if (state === CP.WAITING && key === "exchanged" && theirConfirm(trade, myId)) {
      return "Step 5 of 5: both confirmed, finishing up";
    }
    // Both sides arrange the meetup, so it waits on neither of them alone.
    const who = key === "meetup" ? "" : state === CP.CURRENT ? " for you" : " for them";
    return `Step ${at + 1} of 5: waiting${who} to ${TO_DO[key]}`;
  }
  // Nothing in progress — finished, ended or on hold — so name the last step
  // actually done (the offer always is). A bare "Step 1 of 5" on a declined
  // offer read as a live one, so an ended or held trade says which it is.
  const done = checkpoints.map((c) => c.state).lastIndexOf(CP.DONE);
  const stopped = TERMINAL.has(trade?.status) || isOnHold(trade);
  const lead = stopped ? `${statusLabel(trade.status)} after step` : "Step";
  return `${lead} ${done + 1} of 5: ${checkpoints[done].label.toLowerCase()}`;
}
